import React, { useMemo, useState } from 'react';
import { useLocation, useNavigate } from 'react-router-dom';
import { Section, Eyebrow, Headline, Lead } from '../../components/iss/IssPrimitives';
import {
  DATA_PROTECTION_QUESTIONS,
  isDataProtectionComplete,
  deriveDataProtection,
  getTriggeredActions,
  type DataProtectionAnswers,
} from './dataProtectionQuestions';
import { TEMPLATE_FILES, documentLinkProps, templateHref } from '../templateFile';
import apiService from '../../service/ApiService';
import '../initiation/PocTypePage.scss';

type LocationState = {
  pocId?: string;
  pocType?: string;
  businessJustification?: Record<string, unknown>;
  scorecard?: Record<string, unknown>;
  facilitationSupport?: boolean;
  successCriteria?: unknown;
  environment?: unknown;
  nda?: unknown;
  dataProtection?: {
    answers: DataProtectionAnswers;
    dpaRequired: boolean;
    piaRequired: boolean;
    tiaRequired: boolean;
  };
  itSecurity?: unknown;
};

const DPA_DOCUMENT_LABEL = 'Data Processing Agreement (DPA) template';
const DPA_DOCUMENT_HREF = templateHref(TEMPLATE_FILES.dpa);

const DataProtectionAssessmentPage: React.FC = () => {
  const navigate = useNavigate();
  const location = useLocation();
  const state = (location.state as LocationState | null) ?? {};
  const pocId = state.pocId;


  const [answers, setAnswers] = useState<DataProtectionAnswers>(
    state.dataProtection?.answers ?? {}
  );
  const [attemptedNext, setAttemptedNext] = useState(false);
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [submitError, setSubmitError] = useState<string | null>(null);

  const complete = isDataProtectionComplete(answers);
  const flags = useMemo(() => deriveDataProtection(answers), [answers]);
  const triggeredActions = useMemo(
    () => getTriggeredActions(flags),
    [flags]
  );

  const setYesNo = (code: string, value: boolean) => {
    setAnswers((current) => ({ ...current, [code]: value }));
  };

  const handleNext = async () => {
    setAttemptedNext(true);
    setSubmitError(null);
    if (!complete) return;
  
    if (!pocId) {
      setSubmitError('Missing PoC id. Complete Initiation first.');
      return;
    }
  
    setIsSubmitting(true);
    try {
      const saved = await apiService.saveDataProtection(pocId, {
        q1PersonalData: answers.DP_Q1 === true,
        q2SpecialCategories: answers.DP_Q2 === true,
        q3ThirdParties: answers.DP_Q3 === true,
        q4OutsideEea: answers.DP_Q4 === true,
      });
  
      navigate('/pocs/new/preparation/it-security', {
        state: {
          ...state,
          dataProtection: {
            answers,
            dpaRequired: saved.dpaRequired,
            piaRequired: saved.piaRequired,
            tiaRequired: saved.tiaRequired,
          },
        },
      });
    } catch (err) {
      console.error(err);
      setSubmitError(
        'Could not save data protection. Check Network tab (PoC must be in Preparation).'
      );
    } finally {
      setIsSubmitting(false);
    }
  };

  const answeredCount = DATA_PROTECTION_QUESTIONS.filter(
    (q) => answers[q.code] !== undefined
  ).length;

  return (
    <div className="initiation">
      <aside className="initiation__phases" aria-label="PoC phases">
        <div className="initiation__phase initiation__phase--disabled">
          <span className="initiation__phase-num">1</span>
          <span>Initiation</span>
        </div>
        <div className="initiation__phase initiation__phase--active">
          <span className="initiation__phase-num">2</span>
          <span>Preparation</span>
        </div>
        <div className="initiation__phase initiation__phase--disabled">
          <span className="initiation__phase-num">3</span>
          <span>Running</span>
        </div>
        <div className="initiation__phase initiation__phase--disabled">
          <span className="initiation__phase-num">4</span>
          <span>Evaluation</span>
        </div>
      </aside>

      <div className="initiation__main">
        <nav className="initiation__steps" aria-label="Preparation steps">
          <span className="initiation__step">Step 1</span>
          <span className="initiation__step">Step 2</span>
          <span className="initiation__step">Step 3</span>
          <span className="initiation__step initiation__step--active">
            Step 4
          </span>
          <span className="initiation__step">Step 5</span>
          <span className="initiation__step">Step 6</span>
          <span className="initiation__step">Step 7</span>
          <span className="initiation__step">Review and Submit</span>
        </nav>

        <Section theme="light" spacing="tight">
          <Eyebrow>POC Preparation</Eyebrow>
          <Headline as="h1" size="3xl">
            Data protection assessment
          </Headline>
          <Lead>
            Step 4 — Please answer the following questions with either yes or
            no. Depending on the outcome, you will be asked to take certain
            actions — this could result in signing a DPA or initiating a more
            extensive assessment. If any actions are required, we will direct
            you to the correct document or link.
          </Lead>

          <p className="initiation__note">
            If you are unsure who your local Data Protection Manager is, download
            the most recent DPM overview:{' '}
            <a
              className="scorecard__link"
              {...documentLinkProps(templateHref(TEMPLATE_FILES.dpmOverview))}
            >
              Country overview DPM
            </a>
            .
          </p>

          <div className="scorecard">
            {DATA_PROTECTION_QUESTIONS.map((q) => (
              <div key={q.code} className="scorecard__row">
                <div className="scorecard__prompt">
                  <p>
                    <span className="scorecard__num">{q.number}.</span> {q.text}
                  </p>
                </div>
                <div
                  className="scorecard__answers"
                  role="group"
                  aria-label={q.text}
                >
                  <button
                    type="button"
                    className={
                      'scorecard__yn' +
                      (answers[q.code] === true ? ' scorecard__yn--on' : '')
                    }
                    onClick={() => setYesNo(q.code, true)}
                  >
                    Yes
                  </button>
                  <button
                    type="button"
                    className={
                      'scorecard__yn' +
                      (answers[q.code] === false ? ' scorecard__yn--on' : '')
                    }
                    onClick={() => setYesNo(q.code, false)}
                  >
                    No
                  </button>
                </div>
              </div>
            ))}
          </div>

          {complete ? (
            <div
              className={
                'score-result' +
                (triggeredActions.length === 0
                  ? ' score-result--highest'
                  : ' score-result--medium')
              }
            >
              <p className="score-result__label">Required actions</p>
              {triggeredActions.length === 0 ? (
                <p className="score-result__message">
                  No action is required based on your answers.
                </p>
              ) : (
                <>
                  <p className="score-result__band">Actions triggered</p>
                  <ul className="phase-gate__list">
                    {triggeredActions.map((action) => (
                      <li key={action}>{action}</li>
                    ))}
                  </ul>
                  {flags.dpaRequired ? (
                    <p className="scorecard__help-note">
                      Download the DPA document:{' '}
                      <a
                        className="scorecard__link"
                        {...documentLinkProps(DPA_DOCUMENT_HREF)}
                      >
                        {DPA_DOCUMENT_LABEL}
                      </a>
                    </p>
                  ) : null}
                  <p className="scorecard__help-note">
                    This tool only detects which actions are required. PIA and
                    TIA are separate processes owned by other teams.
                  </p>
                </>
              )}
            </div>
          ) : null}

          <p className="scorecard__progress">
            Answered {answeredCount} of {DATA_PROTECTION_QUESTIONS.length}
          </p>

          {attemptedNext && !complete ? (
            <p className="bj-field__error">
              Answer all four questions before continuing.
            </p>
          ) : null}
          {submitError ? (
            <p className="bj-field__error">{submitError}</p>
          ) : null}

          <div className="initiation__actions">
            <button
              type="button"
              className="initiation__link-back"
              style={{ background: 'none', border: 'none', cursor: 'pointer' }}
              onClick={() =>
                navigate('/pocs/new/preparation/nda', {
                  state: {
                    ...state,
                    dataProtection: complete
                      ? { answers, ...flags }
                      : state.dataProtection,
                  },
                })
              }
            >
              ← Back to NDA
            </button>

            <button
              type="button"
              className="initiation__next"
              disabled={isSubmitting}
              onClick={handleNext}
            >
              {isSubmitting ? 'Saving...' : 'Next →'}
            </button>
          </div>
        </Section>
      </div>
    </div>
  );
};

export default DataProtectionAssessmentPage;
